// Hotspot/Trending API Service
// Handles hotspot tracking across platforms (Weibo, Xiaohongshu, Douyin, WeChat)

import { API_BASE_URL } from '../config/api';

// Supported hotspot platforms
export const HOTSPOT_PLATFORMS = [
    { id: 'weibo', name: '微博热搜', icon: '🔥', color: '#ff8200' },
    { id: 'zhihu', name: '知乎热榜', icon: '📘', color: '#1772f6' },
    { id: 'douyin', name: '抖音热点', icon: '🎵', color: '#000000' },
    { id: 'baidu', name: '百度热搜', icon: '🔎', color: '#2932e1' },
    { id: 'bilibili', name: 'B站热门', icon: '📺', color: '#00a1d6' }
];

// Credit cost for hotspot refresh
export const HOTSPOT_CREDIT_COST = 10;

const normalizeTrend = (item) => ({
    id: item.id || item.topic || `${item.source_name}_${item.heat_score}`,
    topic: item.topic || item.title || '',
    title: item.title || item.topic || '',
    link: item.link || item.url || '',
    url: item.url || item.link || '',
    source_name: item.source_name || '',
    heat_score: Number(item.heat_score || 0),
    view_count: Number(item.view_count || 0),
    summary: item.summary || '',
    keywords: item.keywords || [],
    extra: item.extra || null,
    forecast: item.forecast || 'rising',
    is_commercial: !!item.is_commercial,
    fetched_at: item.fetched_at,
});

/**
 * Get cached hotspots (free)
 * @param {string} platform - Platform ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Cached hotspot list
 */
export const getCachedHotspots = async (platform, token) => {
    const response = await fetch(
        `${API_BASE_URL}/api/v1/hotspot/trends/${platform}/`,
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || '获取热点失败');
    }
    const trends = (data.trends || data.data || []).map(normalizeTrend);
    return {
        ...data,
        data: trends,
    };
};

/**
 * Refresh hotspots (costs 10 credits)
 * @param {string} platform - Platform ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Fresh hotspot list
 */
export const refreshHotspots = async (platform, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/hotspot/trends/`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    const data = await response.json();
    if (!response.ok) {
        if (response.status === 402) {
            throw new Error('积分不足，刷新热点需要10积分');
        }
        throw new Error(data.message || data.error || '刷新热点失败');
    }

    const trends = (data.trends || data.data || []).map(normalizeTrend);
    const filtered = platform ? trends.filter(item => (item.source_name || '').toLowerCase().includes(platform.toLowerCase())) : trends;
    return { ...data, data: filtered };
};

/**
 * Get hotspot details
 * @param {string} hotspotId - Hotspot ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Hotspot details
 */
export const getHotspotDetail = async (hotspotId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/hotspot/trends/${hotspotId}/`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '获取热点详情失败');
    }

    return response.json();
};

/**
 * Generate content from hotspot
 * @param {string} hotspotId - Hotspot ID
 * @param {string} skillId - Skill template to use
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Generated content task
 */
export const generateFromHotspot = async (topic, skillName, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/hotspot/trends/generate/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ topic, skill_name: skillName })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '一键生成失败');
    }

    return response.json();
};

/**
 * Format hotspot heat score for display
 * @param {number} score - Raw heat score
 * @returns {string} - Formatted score (e.g., "1.2万")
 */
export const formatHeatScore = (score) => {
    if (score >= 10000) {
        return `${(score / 10000).toFixed(1)}万`;
    }
    if (score >= 1000) {
        return `${(score / 1000).toFixed(1)}千`;
    }
    return String(score);
};

/**
 * Get platform info by ID
 * @param {string} platformId - Platform ID
 * @returns {Object|null} - Platform info
 */
export const getPlatformInfo = (platformId) => {
    return HOTSPOT_PLATFORMS.find(p => p.id === platformId) || null;
};

const hotspotService = {
    getCachedHotspots,
    refreshHotspots,
    getHotspotDetail,
    generateFromHotspot,
    formatHeatScore,
    getPlatformInfo,
    HOTSPOT_PLATFORMS,
    HOTSPOT_CREDIT_COST
};

export default hotspotService;
